import Checkbox from '@atlaskit/checkbox';
import {
  getDatabaseContainerType,
  getSelectionsExperimentAttributes,
  useAnalytics,
} from '@databases/analytics';
import { useDatabaseContent } from '@databases/database-state';
import { headerCheckboxMetric } from '@databases/frontend-metrics/metrics/header-checkbox';
import { useMetric } from '@databases/frontend-metrics/use-metric';
import { getService } from '@databases/service-container';
import React, { useCallback } from 'react';

import { getExperimentValue } from '@canvas/client-feature-flags';

import { type EntryIsSelectedMap } from './EntryPrefix';

export interface HeaderCheckboxProps {
  entryIsSelectedMap: EntryIsSelectedMap;
  contentId: string;
}

const allCheckboxesUnselected = (entryIsSelectedMap: EntryIsSelectedMap) => {
  return Object.values(entryIsSelectedMap).every((value) => value === false);
};

const HeaderCheckbox: React.FC<HeaderCheckboxProps> = ({
  entryIsSelectedMap,
  contentId,
}) => {
  const { referencing } = useDatabaseContent();
  const analyticsClient = useAnalytics();
  const selectionsActions = getService('SelectionsActions');
  const databaseContainer = getDatabaseContainerType();
  const { fire } = useMetric(headerCheckboxMetric);

  const handleHeaderCheckboxSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const startTime = performance.now();
      const tableRoot = (e.target as HTMLElement).closest('table');

      e.target.blur();

      const allUnselected = allCheckboxesUnselected(entryIsSelectedMap);
      const entryIds = Object.keys(entryIsSelectedMap);

      analyticsClient.sendHeaderCheckboxSelectedEvent({
        contentId,
        databaseContainer,
        entriesCount: entryIds.length,
        checkboxState: allUnselected ? 'selected' : 'unselected',
        ...getSelectionsExperimentAttributes(contentId),
      });

      if (allUnselected) {
        selectionsActions.selectEntries(entryIds, { clearSelectedValues: false });
      } else {
        selectionsActions.deselectAll();
      }

      selectionsActions.clearSelectionIntent();

      const cohort = getExperimentValue('databasesSelectionsPerformance', 'cohort', {
        fireExperimentExposure: false,
      }) as 'control' | 'test';

      fire({
        startTime,
        probeData: {
          tableRoot,
          expectChecked: allUnselected,
        },
        metadata: {
          contentId,
          databasesSelectionsPerformanceCohort: cohort,
        },
        ...(!tableRoot && {
          failImmediately: { reason: 'header-checkbox-dom-sync-missing-table-root' },
        }),
      });
    },
    [
      entryIsSelectedMap,
      analyticsClient,
      contentId,
      databaseContainer,
      selectionsActions,
      fire,
    ]
  );

  const selectedValues = Object.values(entryIsSelectedMap);
  const isChecked = selectedValues.length > 0 && selectedValues.every((value) => value);
  const isIndeterminate =
    selectedValues.length > 0 &&
    selectedValues.some((value) => value) &&
    selectedValues.some((value) => !value);

  return (
    <Checkbox
      onChange={handleHeaderCheckboxSelected}
      isChecked={isChecked}
      isIndeterminate={isIndeterminate}
      testId={isIndeterminate ? 'header-checkbox-indeterminate' : 'header-checkbox'}
      isDisabled={referencing}
      tabIndex={-1}
    />
  );
};
export default HeaderCheckbox;
